/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook used to retrieve the Theme Manager Context.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to get access to react base hooks inside custom hook.
 */
import { useEffect, useCallback, useState } from 'react';

/**
 * Allows for the dispatching of actions to the redux store.
 */
import { useDispatch } from 'react-redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of grade data loaded from the ServiceStack API.
 */
import { SearchSelectors, useSearchSelector, SearchActions, IIndividualSearchState } from 'src/store/reducers/search';

/**
 * Typings
 */
import { IRequestState } from 'src/store/reducers';

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const useGrades = (
    versionId: number,
    limit?: number
): [
    number[],
    Record<number, IIndividualSearchState>,
    number | null,
    IRequestState,
    (query: string) => void,
    (query: string) => void
] => {
    const view = useSearchSelector(SearchSelectors.selectView);
    const data = useSearchSelector(SearchSelectors.selectData);
    const totalCount = useSearchSelector(SearchSelectors.selectTotalCount);
    const loadState = useSearchSelector(SearchSelectors.selectLoadState);

    const [set, setSet] = useState(1);

    const dispatch = useDispatch();

    useEffect(() => {
        return () => {
            dispatch(SearchActions.clear());
        }
    }, [dispatch]);

    const startSearch = useCallback((query?: string) => {
        dispatch(SearchActions.clear());

        if (query !== '') {
            dispatch(SearchActions.search(versionId, query, limit, 1));
        }

        setSet(1);
    }, [versionId, limit, setSet, dispatch]);

    const continueSearch = useCallback((query?: string) => {
        if (query !== '') {
            dispatch(SearchActions.search(versionId, query, limit, set + 1));
        }

        setSet(set + 1);
    }, [versionId, limit, set, setSet, dispatch]);

    return [
        view,
        data,
        totalCount,
        loadState,
        startSearch,
        continueSearch
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default useGrades;


// WEBPACK FOOTER //
// ./src/hooks/useSearch.ts